<!--
  @component LiveTimelineEdge — E1 read-mostly composition.
  composes: ProvenanceIO + ObservabilityIO + LifecycleIO.
  effects:  NONE — pure projection/filter (the dashboard feed).
  purpose:  one subscription point for the operator timeline.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { LIVE_TIMELINE_EDGE_KEY } from "./deployment-edges.js";
  import type {
    LiveTimelineEdgeContext,
    LiveTimelineFilter,
  } from "./deployment-edges.js";
  import type { DeploymentStreamEvent } from "../../nodes/deployment/deployment-io.js";

  // Re-export the contract from local bindings (principle 4 —
  // never re-declare).
  export { LIVE_TIMELINE_EDGE_KEY };
  export type { LiveTimelineEdgeContext, LiveTimelineFilter };

  export function useLiveTimelineEdge(): LiveTimelineEdgeContext {
    const ctx = getContext<LiveTimelineEdgeContext | undefined>(
      LIVE_TIMELINE_EDGE_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useLiveTimelineEdge() called outside of a LiveTimelineEdge tree.",
      );
    }
    return ctx;
  }

  // Arc prefixes — kept in module scope so they're a stable const,
  // not re-allocated per instance.
  const ARC_PREFIXES = [
    "release.",
    "traffic.",
    "db.",
    "storage.",
    "cache.",
    "queue.",
    "telemetry.",
    "rollback.",
  ] as const;
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useProvenanceIO } from "../../nodes/deployment/ProvenanceIO.svelte";
  import { useObservabilityIO } from "../../nodes/deployment/ObservabilityIO.svelte";
  import { useLifecycleIO } from "../../nodes/deployment/LifecycleIO.svelte";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const provenance = useProvenanceIO();
  const observability = useObservabilityIO();
  const lifecycle = useLifecycleIO();

  let filter: LiveTimelineFilter = $state({});

  function matches(e: DeploymentStreamEvent): boolean {
    if (filter.origin !== undefined && e.origin !== filter.origin) {
      return false;
    }
    if (filter.op !== undefined && e.op !== filter.op) return false;
    if (
      filter.arcPrefix !== undefined &&
      !e.op.startsWith(filter.arcPrefix)
    ) {
      return false;
    }
    return true;
  }

  const ctx: LiveTimelineEdgeContext = {
    get events() {
      return provenance.events.filter(matches);
    },
    get digest() {
      return observability.digest();
    },
    get phases() {
      return lifecycle.phases;
    },
    get completeCount() {
      return lifecycle.completeCount;
    },
    get latestByArc() {
      const out: Record<string, DeploymentStreamEvent | null> = {};
      for (const prefix of ARC_PREFIXES) {
        let latest: DeploymentStreamEvent | null = null;
        for (const e of provenance.events) {
          if (e.op.startsWith(prefix)) latest = e;
        }
        out[prefix] = latest;
      }
      return out;
    },
    get filter() {
      return filter;
    },
    setFilter(next) {
      filter = next;
    },
    clearFilter() {
      filter = {};
    },
  } satisfies LiveTimelineEdgeContext;

  setContext(LIVE_TIMELINE_EDGE_KEY, ctx);
</script>

{@render children?.()}
